// @flow
import React, { Fragment } from 'react';
import { isEmpty } from 'lodash';
import classnames from 'classnames';

import { Badge } from 'Components/frameworks/badge/Badge';
import { BlockLink } from 'Components/frameworks/block-link/BlockLink';
import { Button } from 'Components/frameworks/button/Button';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Price } from 'Components/frameworks/price/Price';
import { Spinner } from 'Components/frameworks/spinner/Spinner';
import { TemplateString } from 'Components/frameworks/template-string/TemplateString';
import { Title } from 'Components/frameworks/title/Title';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { MIN_HEIGHT, MIN_HEIGHT_LITE } from 'Components/widgets/price-ticket/PriceTicket';
import { type PriceTicketContentProps } from 'Components/widgets/price-ticket/PriceTicketContentWrapper';
import { t } from 'Src/i18n';

export const PriceTicketLarge = ({
  button,
  badgeIcon,
  className,
  hasCallCenter = false,
  info,
  isLite = false,
  isLoading = false,
  items = [],
  price,
  staticPrice,
  subtitle = '',
  title,
  travelAdvisorConfig,
}: {
  ...PriceTicketContentProps,
  isLite: boolean,
}) => {
  const minHeight = isLite ? MIN_HEIGHT_LITE : MIN_HEIGHT;

  // eslint-disable-next-line clubmed/no-fake-component
  function renderTicket() {
    const classes = isLite ? 'shadow' : 'p-1';
    const hasDetail = !isLite && !hasCallCenter && (!isEmpty(items) || !isEmpty(info));

    return isLoading ? (
      <div
        className="bg-white flex items-center justify-center opacity-75 p-4"
        style={{ minHeight }}
      >
        <Spinner size="small" color={COLORS.BLUE} />
      </div>
    ) : (
      <Fragment>
        <div
          className={classnames(
            'bg-white border-t-1 border-gray-light flex items-center',
            classes,
            { 'border-b-1 ': !isLite },
          )}
          style={{ minHeight }}
        >
          <Badge {...badgeIcon} />
          <div className="flex-1 mx-5">
            <Title
              className="font-brand font-normal leading-tight text-2xl"
              color={COLORS.GRAY_DARKER}
              hLevel={2}
              theme="none"
            >
              {title}
            </Title>
            <Title
              className="font-sans font-bold leading-tight text-md uppercase"
              color={COLORS.GRAY_DARKER}
              hLevel={3}
              theme="none"
            >
              {subtitle}
            </Title>
          </div>
          {!hasCallCenter &&
            !isEmpty(price) && (
              <Price
                {...price}
                from={t('price.format.from')}
                instead={t('price.format.instead')}
                className={classnames('flex items-center mx-5', {
                  'opacity-0 pointer-events-none': isLite,
                })}
                hasInitialPrice={false}
                theme="ticket"
              />
            )}
          {staticPrice && (
            <TemplateString
              className="text-gray-darker text-base font-sans leading-one"
              valuesClassName={{ amount: 'text-2xl underline font-brand leading-tight' }}
              template={staticPrice.price}
              values={{ amount: staticPrice.amount }}
            />
          )}
          {hasCallCenter &&
            travelAdvisorConfig && (
              <BlockLink
                texts={travelAdvisorConfig.text}
                url={travelAdvisorConfig.href}
                className={classnames('mx-5 text-gray-darker', {
                  'opacity-0 pointer-events-none': isLite,
                })}
                icon={ICONS.PHONE_BUBBLE}
              />
            )}
          <Button {...button} className="mx-5" />
        </div>
        {hasDetail && (
          <a
            href={button && button.url}
            className="reset-anchor bg-gray-lighter flex items-stretch p-3"
          >
            {items.map(({ description, iconName, label = '' }, idx) => (
              <Fragment key={`${label}-${description}`}>
                {idx > 0 && <div className="bg-gray-light w-px" />}
                <div className="flex items-center px-3 w-1/5" data-testid="PriceTicketLarge-item">
                  <Icon color={COLORS.BLUE} name={iconName} width="1.5rem" />
                  <div className="font-sans text-sm rtl:ml-4">
                    <div>{label}</div>
                    <div className="font-bold text-blue">{description}</div>
                  </div>
                </div>
              </Fragment>
            ))}
            {!isEmpty(info) && (
              <Fragment>
                {!isEmpty(items) && <div className="bg-gray-light w-px" />}
                <div className="rtl:ml-auto w-1/5" data-testid="PriceTicketLarge-info">
                  <div className="flex items-center px-3">
                    <Icon color={COLORS.BLUE} name={info.iconName} width="1.5rem" />
                    <div className="font-brand leading-one text-blue text-xl rtl:ml-2">
                      {info.label}
                    </div>
                  </div>
                </div>
              </Fragment>
            )}
          </a>
        )}
      </Fragment>
    );
  }

  if (!title) {
    return null;
  }

  return (
    <div
      className={classnames(className, 'fadeInLong relative', { 'm-auto max-w-site': isLite })}
      data-testid="PriceTicketLarge"
    >
      {isLite ? renderTicket() : <SiteContainer>{renderTicket()}</SiteContainer>}
    </div>
  );
};
